import {MissingTranslationHandler, MissingTranslationHandlerParams} from "@ngx-translate/core";
import {inject, Injectable} from "@angular/core";
import {LoggingService} from "../logger/logging.service";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {environment} from "../../../environments/environment";

@Injectable({providedIn: 'root'})
export class AccMissingTranslationHandler implements MissingTranslationHandler {

  private readonly log: LoggingService = inject(LoggingService);

  private readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  private readonly ignore: string[] = [
    'contact.relation.type.null',
    'contact.relation.type.undefined',
  ];

  // English fallbacks for keys rendered on bootstrap/loading screens before the
  // async Tolgee translations have loaded — avoids spurious "not found" warnings
  // until the localized translation arrives and the 'translate' pipe re-evaluates.
  private readonly bootstrapFallback: Record<string, string> = {
    'wait': 'Please wait ...',
  };

  handle(params: MissingTranslationHandlerParams): any {
    if (nonBlank(params.key) && this.ignore.indexOf(params.key) === -1) {
      const fallback: string | undefined = this.bootstrapFallback[params.key];
      if (isDefined(fallback)) {
        return fallback;
      }
      // While the active language bundle is still loading, every key resolves as "missing".
      // The 'translate' pipe re-evaluates once the async bundle arrives, so suppress the noise.
      if (!this.isIronBackupViewer && this.isLanguageLoaded(params)) {
        this.log.warn('Translation for key "{}" not found', [params.key]);
        if (this.log.isDebugEnabled()) {
          console.trace();
        }
      }
      return params.key;
    }
    return '';
  }

  private isLanguageLoaded(params: MissingTranslationHandlerParams): boolean {
    // 'store' and 'getActiveRequestedLang' are internal to TranslateService. A lookup uses the language just requested,
    // and getCurrentLang() keeps the old one until it has loaded - checking that warned on every language switch.
    const service: any = params.translateService as any;
    const lang: string | null = service.getActiveRequestedLang?.() ?? params.translateService.getCurrentLang();
    return isDefined(service.store) && nonBlank(lang) && service.store.hasTranslationFor(lang);
  }
}
